import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft, ChevronRight, Briefcase, Car, Coffee, Hotel,
  Utensils, Users, Loader2, CalendarDays, Download, Palmtree, HeartPulse,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m > 0 ? m + "m" : ""}`.trim();
  return `${m}m`;
}

function fmtDurationFull(seconds) {
  if (!seconds || seconds <= 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const MONTH_NAMES = [
  "Januar", "Februar", "Maerz", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export default function Monatsansicht() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/events/monthly-report?month=${monthKey}`);
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [monthKey]);

  useEffect(() => { loadData(); }, [loadData]);

  const shiftMonth = (dir) => {
    let newMonth = month + dir;
    let newYear = year;
    if (newMonth < 1) { newMonth = 12; newYear--; }
    if (newMonth > 12) { newMonth = 1; newYear++; }
    setMonth(newMonth);
    setYear(newYear);
  };

  const downloadPdf = async () => {
    try {
      const res = await authFetch(`${API}/events/monthly-report/pdf?month=${monthKey}`);
      if (!res.ok) { alert("PDF-Fehler"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Monatsbericht_${monthKey}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("PDF-Download fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4" data-testid="monatsansicht-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--tga-blue)", opacity: 0.15 }}>
            <CalendarDays className="w-5 h-5" style={{ color: "var(--tga-blue)" }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: "var(--tga-text-primary)" }}>Monatsuebersicht</h1>
            <p className="text-xs" style={{ color: "var(--tga-text-muted)" }}>Lohnbuchhaltung - Arbeitsstunden, Fahrten & Verpflegung</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={downloadPdf}
            className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }}
            title="PDF Export"
            data-testid="monthly-pdf-export-btn"
          >
            <Download className="w-4 h-4" />
          </button>
          <button onClick={() => shiftMonth(-1)} className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="month-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-1.5 text-sm font-semibold rounded-lg" style={{ backgroundColor: "var(--tga-bg-elevated)", color: "var(--tga-text-primary)" }}
            data-testid="month-label"
          >
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button onClick={() => shiftMonth(1)} className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="month-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--tga-blue)" }} />
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Mitarbeiter</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: "var(--tga-text-primary)" }}>{data.employees?.length || 0}</p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Gesamtarbeit</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">
                {fmtDurationFull(data.totals?.work_seconds || 0)}
              </p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Car className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Gesamtfahrt</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">
                {fmtDurationFull(data.totals?.travel_seconds || 0)}
              </p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Utensils className="w-4 h-4 text-orange-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Verpflegung</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">{data.totals?.meal_allowance || 0} EUR</p>
            </div>
          </div>

          {/* Table */}
          {(!data.employees || data.employees.length === 0) ? (
            <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--tga-text-dim)" }} />
              <p className="text-sm" style={{ color: "var(--tga-text-muted)" }}>Keine Mitarbeiter mit gefuehrtem Arbeitstag aktiv</p>
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="monthly-table">
                  <thead>
                    <tr style={{ backgroundColor: "var(--tga-bg-elevated)", borderBottom: "2px solid var(--tga-border-strong)" }}>
                      <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)", minWidth: 160 }}>Mitarbeiter</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Arbeit</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Fahrt</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Pause</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Verpflegung</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Arbeitstage</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Urlaub</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Krank</th>
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)" }}>Hotel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.employees.map((emp) => {
                      const t = emp.totals || {};
                      return (
                        <tr key={emp.user_id} style={{ borderBottom: "1px solid var(--tga-border)" }} data-testid={`monthly-row-${emp.user_id}`}>
                          <td className="px-3 py-3">
                            <p className="text-[13px] font-medium" style={{ color: "var(--tga-text-bright)" }}>{emp.user_name}</p>
                            <p className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>{emp.role}</p>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Briefcase className="w-3.5 h-3.5 text-emerald-400" />
                              <span className="text-[13px] font-mono font-semibold text-emerald-400">{fmtDurationFull(t.work_seconds)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Car className="w-3.5 h-3.5 text-blue-400" />
                              <span className="text-[13px] font-mono" style={{ color: "var(--tga-text-secondary)" }}>{fmtDuration(t.travel_seconds)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {t.pause_seconds > 0 ? (
                              <div className="flex items-center justify-center gap-1">
                                <Coffee className="w-3.5 h-3.5 text-amber-400" />
                                <span className="text-[13px] font-mono" style={{ color: "var(--tga-text-dim)" }}>{fmtDuration(t.pause_seconds)}</span>
                              </div>
                            ) : <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {t.meal_allowance > 0 ? (
                              <div>
                                <div className="flex items-center justify-center gap-1">
                                  <Utensils className="w-3.5 h-3.5 text-orange-400" />
                                  <span className="text-[13px] font-bold text-orange-400">{t.meal_allowance} EUR</span>
                                </div>
                                <span className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>{t.meal_count}x</span>
                              </div>
                            ) : <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="text-[13px] font-semibold" style={{ color: "var(--tga-text-primary)" }}>{t.working_days}</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {t.vacation_days > 0 ? (
                              <div className="flex items-center justify-center gap-1">
                                <Palmtree className="w-3.5 h-3.5 text-green-400" />
                                <span className="text-[13px] font-semibold text-green-400">{t.vacation_days}</span>
                              </div>
                            ) : <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {t.sick_days > 0 ? (
                              <div className="flex items-center justify-center gap-1">
                                <HeartPulse className="w-3.5 h-3.5 text-red-400" />
                                <span className="text-[13px] font-semibold text-red-400">{t.sick_days}</span>
                              </div>
                            ) : <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {t.hotel_days > 0 ? (
                              <div className="flex items-center justify-center gap-1">
                                <Hotel className="w-3.5 h-3.5 text-indigo-400" />
                                <span className="text-[13px] font-semibold text-indigo-400">{t.hotel_days}</span>
                              </div>
                            ) : <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Totals Footer */}
                  <tfoot>
                    <tr style={{ borderTop: "2px solid var(--tga-border-strong)", backgroundColor: "var(--tga-bg-elevated)" }}>
                      <td className="px-3 py-3">
                        <span className="text-[12px] font-bold uppercase" style={{ color: "var(--tga-text-muted)" }}>Gesamt</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-mono font-bold text-emerald-400">{fmtDurationFull(data.totals?.work_seconds)}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-mono font-bold text-blue-400">{fmtDurationFull(data.totals?.travel_seconds)}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-mono font-bold" style={{ color: "var(--tga-text-dim)" }}>{fmtDuration(data.totals?.pause_seconds)}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-bold text-orange-400">{data.totals?.meal_allowance || 0} EUR</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-bold" style={{ color: "var(--tga-text-primary)" }}>{data.totals?.working_days || 0}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-bold text-green-400">{data.employees?.reduce((s, e) => s + (e.totals?.vacation_days || 0), 0) || 0}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-bold text-red-400">{data.employees?.reduce((s, e) => s + (e.totals?.sick_days || 0), 0) || 0}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[13px] font-bold text-indigo-400">{data.employees?.reduce((s, e) => s + (e.totals?.hotel_days || 0), 0) || 0}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
